import {controller, attr, target} from '@github/catalyst'

@controller
class PasskeyVerificationElement extends HTMLElement {
  @attr verificationUrl: string = ''
  @attr returnTo: string = ''
  @attr csrfToken: string = ''
  @attr cancelMessage: string = ''
  @attr errorMessage: string = ''
  @target error: HTMLElement

  connectedCallback() {
    this.startVerification()
  }

  async startVerification() {
    try {
      const optionsResponse = await fetch(this.verificationUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!optionsResponse.ok) {
        throw new Error('Failed to get verification options')
      }

      const options = await optionsResponse.json()

      options.challenge = this.base64ToArrayBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map((c: {id: string, type: string}) => ({
          ...c,
          id: this.base64ToArrayBuffer(c.id)
        }))
      }

      const credential = await navigator.credentials.get({
        publicKey: options
      }) as PublicKeyCredential

      if (!credential) {
        throw new Error('Failed to get credential')
      }

      const response = credential.response as AuthenticatorAssertionResponse

      const verifyUrl = this.verificationUrl.replace('/new', '').replace('.json', '')
      const verifyResponse = await fetch(verifyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          id: credential.id,
          rawId: this.arrayBufferToBase64(credential.rawId),
          type: credential.type,
          response: {
            authenticatorData: this.arrayBufferToBase64(response.authenticatorData),
            clientDataJSON: this.arrayBufferToBase64(response.clientDataJSON),
            signature: this.arrayBufferToBase64(response.signature)
          }
        })
      })

      const result = await verifyResponse.json()

      if (result.success) {
        window.location.href = result.redirect_to || this.returnTo || '/'
      } else {
        this.showError(result.error || 'Verification failed')
      }
    } catch (error) {
      console.error('Passkey verification error:', error)
      if ((error as Error).name === 'NotAllowedError') {
        this.showError(this.cancelMessage)
      } else {
        this.showError(this.errorMessage)
      }
    }
  }

  private showError(message: string) {
    this.error.textContent = message
    this.error.hidden = false
  }


  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }
}
